<template>
  <div class="creative-info flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center gap-1">
      <h2 class="font-bold p-0 mb-0">{{ creative?.name }}</h2>
      <ElButton
        v-if="$is_GD"
        :disabled="!canDeleteCreative(creative?.status)"
        @click="onDeleteClick"
        type="text"
        :class="['w-[40px] h-[40px] flex justify-center items-center', { 'is-disabled opacity-50': !canDeleteCreative(creative?.status) }]"
      >
        <svg class="text-gray" width="12" height="12" fill="none">
          <use :xlink:href="`#delete-outline`"></use>
        </svg>
      </ElButton>
    </div>

    <!-- Status -->
    <CampaignStatus class="status" :status="creative?.status" />

    <!-- File size -->
    <div v-if="creative?.files.length" class="w-full mt-2 block">Size: {{ formattedTotalSize }}</div>

    <!-- Section Title -->
    <div class="my-2">
      <p class="font-bold text-15px mb-0">
        {{ showRevisionInstructions ? 'Revision Instructions' : 'Activity' }}
      </p>
    </div>

    <!-- Revision Instructions -->
    <div v-if="showRevisionInstructions" class="h-full">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item prop="instructions" required>
          <el-input
            v-model="form.instructions"
            type="textarea"
            :rows="4"
            :maxlength="400"
            show-word-limit
            placeholder="Please describe the changes required"
          />
        </el-form-item>
      </el-form>

      <ElButton :disabled="!this.form.instructions" type="primary" class="mt-2 w-full" icon="el-icon-top-left" @click="submitRevision">
        Request revision
      </ElButton>
      <ElButton class="w-full mt-1 ml-0" @click="showRevisionInstructions = false"> Cancel </ElButton>
    </div>

    <!-- Activity -->
    <template v-else>
      <Activity :activity="creative?.activity || []" />
    </template>

    <!-- Action Buttons -->
    <ElButton
      v-if="$is_GD"
      :disabled="!canSendToReview(creative?.status)"
      type="primary"
      icon="el-icon-s-promotion"
      class="w-full mt-3"
      @click="$emit('send-to-review')"
    >
      Send to review
    </ElButton>
    <template v-if="!showRevisionInstructions">
      <ElButton
        type="primary"
        icon="el-icon-top-left"
        class="w-full mt-1 ml-0 bg-primary/10 text-primary"
        v-if="isInRequestRevision(creative?.status) || canRequestRevisionOrApprove(creative?.status)"
        :disabled="!canRequestRevisionOrApprove(creative?.status)"
        @click="showRevisionInstructions = true"
      >
        Request revision
      </ElButton>

      <ElButton
        v-if="isInRequestRevision(creative?.status) || canRequestRevisionOrApprove(creative?.status)"
        :disabled="!canRequestRevisionOrApprove(creative?.status)"
        type="primary"
        icon="el-icon-check"
        class="w-full mt-1 ml-0"
        @click="$emit('approve')"
      >
        Approve
      </ElButton>
    </template>
    <ElButton
      v-if="canDownload(creative?.status)"
      :loading="isDownloading"
      type="primary"
      icon="el-icon-download"
      size="small"
      class="w-full mt-1 ml-0"
      @click="downloadCreativeFile(creative)"
    >
      Download
    </ElButton>
  </div>
</template>

<script>
import CampaignStatus from '@/components/social-products/social-boost/parts/CampaignStatus.vue'
import CreativesMixin from '@/mixins/social-products/Creatives.js'
import Activity from './Activity.vue'

export default {
  name: 'CreativeInfo',
  mixins: [CreativesMixin],
  components: {
    CampaignStatus,
    Activity,
  },
  props: {
    creative: {
      type: Object,
      default: () => null,
    },
    flightStatus: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      showRevisionInstructions: false,
      form: {
        instructions: '',
      },
      rules: {
        instructions: [{ required: true, message: 'Please enter revision instructions', trigger: 'blur' }],
      },
    }
  },
  computed: {
    totalSize() {
      if (!this.creative?.files?.length) return 0
      return this.creative.files.reduce((sum, file) => sum + (file.size || 0), 0)
    },
    formattedTotalSize() {
      const size = this.totalSize
      if (!size || isNaN(size)) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(size) / Math.log(1024))
      return (size / Math.pow(1024, i)).toFixed(1) + ' ' + units[i]
    },
  },
  methods: {
    onDeleteClick() {
      this.$emit('delete')
    },
    submitRevision() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return
        this.$emit('request-revision', {
          creative_id: this.creative.id,
          instructions: this.form.instructions.trim(),
        })

        this.form.instructions = ''
        this.showRevisionInstructions = false
      })
    },
  },
}
</script>

<style lang="scss">
.creative-info {
  .status {
    span {
      float: none;
      margin: 0;
    }
  }
}
</style>
